import React from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import ForumIcon from '@material-ui/icons/Forum';
import ListAltIcon from '@material-ui/icons/ListAlt';
import StorefrontIcon from '@material-ui/icons/Storefront';
import { useCurrentUserInfo } from '../../hooks/useCurrentUserInfo';
import { PLAN_TYPE } from '../../config';
import { useSelector } from 'react-redux';
import {
  getStripePriceSelector,
  getStripeOriginalPriceSelector,
  getStripeSavingsSelector
} from '../../redux/features/configuration/configurationSlice';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  headerSection: {
    marginBottom: theme.spacing(2)
  },
  headerTitle: {
    fontSize: '1.4rem',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary
  },
  headerSubtitle: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5
  },
  tiersGrid: {
    marginTop: theme.spacing(3)
  },
  basePlanSection: {
    marginBottom: theme.spacing(2)
  },
  basePlanTitle: {
    fontSize: '2.5rem',
    fontWeight: 700,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
    textAlign: 'center',
    lineHeight: 1.2,
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem'
    }
  },
  basePlanGrid: {
    marginTop: theme.spacing(2)
  },
  basePlanCard: {
    padding: theme.spacing(2.5),
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    height: '100%',
    boxShadow: theme.shadows[1]
  },
  basePlanIcon: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: `${theme.palette.primary.main}0F`,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1.5)
  },
  basePlanCardTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary
  },
  basePlanCardBody: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.6
  },
  featureIcon: {
    minWidth: 26,
    color: '#9aa0a6',
    '& svg': {
      fontSize: '1rem'
    }
  },
  featureText: {
    '& .MuiListItemText-primary': {
      fontSize: '0.75rem',
      fontWeight: 500,
      color: theme.palette.text.primary,
      lineHeight: 1.4
    }
  },
  tierCard: {
    height: '100%',
    position: 'relative',
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1.5),
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    overflow: 'visible',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[6]
    }
  },
  tierCardSelected: {
    borderColor: theme.palette.primary.main,
    borderWidth: 3,
    boxShadow: theme.shadows[4],
    background: `${theme.palette.primary.main}05`
  },
  tierCardPopular: {
    borderColor: theme.palette.divider
  },
  tierHighlightBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 8,
    borderTopLeftRadius: theme.spacing(1.5),
    borderTopRightRadius: theme.spacing(1.5)
  },
  tierHighlightGreen: {
    backgroundColor: '#7BE89C'
  },
  tierHighlightBlue: {
    backgroundColor: '#3D63DD'
  },
  tierBadge: {
    backgroundColor: '#7BE89C',
    color: '#0b0b0b',
    fontWeight: 700,
    fontSize: '0.65rem',
    padding: theme.spacing(0.4, 1.4),
    borderRadius: theme.spacing(2),
    whiteSpace: 'nowrap',
    marginLeft: theme.spacing(1)
  },
  tierHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5)
  },
  tierName: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.2
  },
  tierPricing: {
    marginBottom: theme.spacing(1.5)
  },
  tierPrice: {
    fontSize: '1.8rem',
    fontWeight: 800,
    color: '#111111',
    lineHeight: 1
  },
  tierOriginalPrice: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    textDecoration: 'line-through',
    marginLeft: theme.spacing(0.75)
  },
  tierSavings: {
    display: 'inline-block',
    marginTop: theme.spacing(0.75),
    padding: 0,
    backgroundColor: 'transparent',
    color: theme.palette.success.main,
    borderBottom: `2px solid ${theme.palette.success.main}`,
    fontSize: '0.75rem',
    fontWeight: 700,
    borderRadius: 0
  },
  tierDescription: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.4
  },
  tierFeatures: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    flex: 1
  },
  tierFeatureItem: {
    padding: theme.spacing(0.5, 0),
    paddingLeft: 0,
    alignItems: 'flex-start'
  },
  selectButton: {
    width: '100%',
    padding: theme.spacing(1.2),
    fontSize: '0.875rem',
    fontWeight: 700,
    borderRadius: theme.spacing(4),
    textTransform: 'none',
    background: '#0b0b0b',
    color: '#fff',
    transition: 'all 0.3s ease',
    marginBottom: theme.spacing(1),
    '&:hover': {
      background: '#1a1a1a',
      boxShadow: theme.shadows[4]
    }
  },
  buttonReassurance: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    fontWeight: 500,
    lineHeight: 1.4,
    marginTop: theme.spacing(0.5)
  },
  selectedButton: {
    background: '#0b0b0b',
    '&:hover': {
      background: '#1a1a1a'
    }
  },
  divider: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(8),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  colorPickerSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`
  },
  colorPickerLabel: {
    fontSize: '0.875rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  colorPickerWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(1)
  },
  selectedColorPreview: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: `2px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[1]
  },
  reassuranceSection: {
    marginTop: theme.spacing(6),
    padding: 0,
    background: 'transparent',
    borderRadius: 0,
    border: 'none'
  },
  reassuranceTitle: {
    fontSize: '2.5rem',
    fontWeight: 700,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
    textAlign: 'center',
    lineHeight: 1.2,
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem'
    }
  },
  reassuranceList: {
    padding: 0,
    listStyle: 'none',
    margin: 0
  },
  reassuranceGrid: {
    marginTop: theme.spacing(2)
  },
  reassuranceCard: {
    padding: theme.spacing(2.5),
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    height: '100%',
    boxShadow: theme.shadows[1]
  },
  reassuranceCardTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary
  },
  reassuranceCardBody: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.6
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '2rem',
    marginBottom: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem'
    }
  }
}));

const PricingTierCards = ({
  selectedTier,
  onTierSelect,
  brandColor,
  onBrandColorChange,
  currentPlanId = 0, // 0 = no plan, 9 = Starter, 10 = Professional, 11 = Premium
  bundleOnly = false
}) => {
  const classes = useStyles();

  // Fetch pricing from Redux store
  const starterPrice = useSelector(getStripePriceSelector(PLAN_TYPE.HIRING_PROFILE_STARTER));
  const starterOriginalPrice = useSelector(getStripeOriginalPriceSelector(PLAN_TYPE.HIRING_PROFILE_STARTER));
  const starterSavings = useSelector(getStripeSavingsSelector(PLAN_TYPE.HIRING_PROFILE_STARTER));

  const professionalPrice = useSelector(getStripePriceSelector(PLAN_TYPE.HIRING_PROFILE_PROFESSIONAL));
  const professionalOriginalPrice = useSelector(getStripeOriginalPriceSelector(PLAN_TYPE.HIRING_PROFILE_PROFESSIONAL));
  const professionalSavings = useSelector(getStripeSavingsSelector(PLAN_TYPE.HIRING_PROFILE_PROFESSIONAL));

  const premiumPrice = useSelector(getStripePriceSelector(PLAN_TYPE.HIRING_PROFILE_PREMIUM));
  const premiumOriginalPrice = useSelector(getStripeOriginalPriceSelector(PLAN_TYPE.HIRING_PROFILE_PREMIUM));
  const premiumSavings = useSelector(getStripeSavingsSelector(PLAN_TYPE.HIRING_PROFILE_PREMIUM));

  const tiers = [
    {
      id: 'starter',
      name: 'Startup Pack',
      price: starterPrice,
      originalPrice: starterOriginalPrice,
      savings: starterSavings,
      description: '3 Job Posts',
      badge: 'Most Popular',
      highlightTone: 'green',
      features: [
        'Publish up to 3 jobs',
        'Use anytime, no expiration',
        'Ideal for hiring your first roles',
        'Full hiring workspace included'
      ],
      planId: PLAN_TYPE.HIRING_PROFILE_STARTER
    },
    {
      id: 'professional',
      name: 'Team Pack',
      price: professionalPrice,
      originalPrice: professionalOriginalPrice,
      savings: professionalSavings,
      description: '10 Job Posts',
      badge: '',
      highlightTone: 'green',
      features: [
        'Publish up to 10 jobs',
        'Best for ongoing hiring',
        'Share across roles and teams',
        'Lower cost per job post'
      ],
      planId: PLAN_TYPE.HIRING_PROFILE_PROFESSIONAL
    },
    {
      id: 'premium',
      name: 'Growth Pack',
      price: premiumPrice,
      originalPrice: premiumOriginalPrice,
      savings: premiumSavings,
      description: '25 Job Posts',
      badge: '',
      highlightTone: 'blue',
      features: [
        'Publish up to 25 jobs',
        'Built for fast-growing teams',
        'High-volume hiring ready',
        'Jobs available whenever you need them'
      ],
      planId: PLAN_TYPE.HIRING_PROFILE_PREMIUM
    }
  ];
  const basePlanFeatures = [
    {
      title: 'Candidate pipeline',
      description: 'Drag-and-drop workflow to move applicants through stages.',
      icon: <ListAltIcon fontSize="small" />
    },
    {
      title: 'Company branding',
      description: 'Your logo and company details show on every job post.',
      icon: <StorefrontIcon fontSize="small" />
    },
    {
      title: 'Interview scheduling',
      description: 'Schedule interviews with automatic reminders and updates.',
      icon: <EventAvailableIcon fontSize="small" />
    },
    {
      title: 'Direct messaging',
      description: 'Chat with candidates without leaving the platform.',
      icon: <ForumIcon fontSize="small" />
    }
  ];
  const reassuranceFeatures = [
    {
      title: 'One-time payment',
      description: 'No subscriptions or recurring fees.'
    },
    {
      title: 'Full hiring workspace',
      description: 'Built-in candidate tracking and interviews.'
    },
    {
      title: 'Direct messaging',
      description: 'Message candidates without leaving the platform.'
    },
    {
      title: 'Control visibility',
      description: 'Decide when and where your jobs appear.'
    },
    {
      title: 'Hire faster',
      description: 'Move quickly without recruiter fees.'
    }
  ];

  const handleTierClick = (tier) => {
    onTierSelect(tier);
  };
  const { isLogin } = useCurrentUserInfo();
  return (
    <Box className={classes.root}>
      {/* Tier Cards */}
      <Grid container spacing={2} className={classes.tiersGrid}>
        {tiers.map((tier) => {
          const isCurrent = currentPlanId === tier.planId;

          return (
            <Grid item xs={12} sm={6} md={4} key={tier.id}>
              <Card
                className={`${classes.tierCard} ${
                  selectedTier === tier.id ? classes.tierCardSelected : ''
                } ${tier.badge ? classes.tierCardPopular : ''}`}
                onClick={() => handleTierClick(tier)}
                style={{
                  cursor: 'pointer'
                }}>
                {tier.highlightTone && (
                  <Box
                    className={`${classes.tierHighlightBar} ${
                      tier.highlightTone === 'blue'
                        ? classes.tierHighlightBlue
                        : classes.tierHighlightGreen
                    }`}
                  />
                )}
                <CardContent
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}>
                  <Box className={classes.tierHeader}>
                    <Typography className={classes.tierName}>
                      {tier.name}
                    </Typography>
                    {tier.badge && !isCurrent && (
                      <Chip
                        label={tier.badge}
                        className={classes.tierBadge}
                        size="small"
                      />
                    )}
                  </Box>

                  <Box className={classes.tierPricing}>
                    <Box display="flex" alignItems="baseline">
                      <Typography className={classes.tierPrice}>
                        ${tier.price}
                      </Typography>
                      <Typography className={classes.tierOriginalPrice}>
                        ${tier.originalPrice}
                      </Typography>
                    </Box>
                    <Chip
                      label={`Save $${tier.savings}`}
                      className={classes.tierSavings}
                      size="small"
                    />
                  </Box>

                  <Typography className={classes.tierDescription}>
                    <strong>{tier.description}</strong>
                  </Typography>

                  <List className={classes.tierFeatures}>
                    {tier.features.map((feature, index) => (
                      <ListItem key={index} className={classes.tierFeatureItem}>
                        <ListItemIcon className={classes.featureIcon}>
                          <CheckCircleIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          className={classes.featureText}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Box>
                    <Button
                      variant="contained"
                      className={`${classes.selectButton} ${
                        selectedTier === tier.id ? classes.selectedButton : ''
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTierClick(tier);
                      }}>
                      {!isLogin
                        ? 'Start Hiring'
                        : selectedTier === tier.id
                        ? '✓ Selected'
                        : 'Select pack'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <Box className={classes.divider} />
      {/* Base Plan Features */}
      <Box className={classes.basePlanSection}>
        <Typography className={classes.basePlanTitle}>
          What every plan gets you
        </Typography>
        <Grid container spacing={2} className={classes.basePlanGrid}>
          {basePlanFeatures.map((feature) => (
            <Grid item xs={12} sm={6} md={3} key={feature.title}>
              <Box className={classes.basePlanCard}>
                <Box className={classes.basePlanIcon}>{feature.icon}</Box>
                <Typography className={classes.basePlanCardTitle}>
                  {feature.title}
                </Typography>
                <Typography className={classes.basePlanCardBody}>
                  {feature.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box className={classes.divider} />
      {/* Why Companies Choose Us - Reassurance Section */}
      <Box className={classes.reassuranceSection}>
        <Typography className={classes.reassuranceTitle}>
          Why Companies Choose Us
        </Typography>
        <Grid container spacing={2} className={classes.reassuranceGrid}>
          {reassuranceFeatures.map((feature) => (
            <Grid item xs={12} sm={6} md={4} key={feature.title}>
              <Box className={classes.reassuranceCard}>
                <Typography className={classes.reassuranceCardTitle}>
                  {feature.title}
                </Typography>
                <Typography className={classes.reassuranceCardBody}>
                  {feature.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default PricingTierCards;
